import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { LocalTypeahead } from '@/components/common/local-typeahead';
import { Button, Modal, Text } from '@/components/ui';
import { type VendorResolution } from '@/services/qb-compile-client';
import { formatMoneyCents } from '@/utils/format-money';
import { type UnresolvedVendor } from '@/utils/qb-compile';

export interface UnknownVendorModalProps {
	visible: boolean;
	/** Distinct unknown payees from the probe compile. */
	unresolved: UnresolvedVendor[];
	/** Global vendor list to map against. */
	vendors: Array<{ id: string; name: string }>;
	/** Confirmed decisions, one per unresolved payee (all rows resolved). */
	onResolve: (decisions: VendorResolution[]) => void;
	onCancel: () => void;
}

const keyOf = (name: string): string => name.trim().toLowerCase();

/**
 * Prompts the user to resolve every payee the QuickBooks import couldn't match
 * to a vendor. Each row defaults to "Create new vendor" (named after the QB
 * payee); the user can instead map it onto an existing vendor via typeahead.
 * Confirm stays disabled until every row is resolved, so the re-compile can
 * never hit an unknown vendor again.
 */
export const UnknownVendorModal: React.FC<UnknownVendorModalProps> = ({
	visible, unresolved, vendors, onResolve, onCancel,
}) => {
	const [decisions, setDecisions] = useState<Record<string, VendorResolution>>(() => {
		const init: Record<string, VendorResolution> = {};
		for (const u of unresolved) init[keyOf(u.name)] = { name: u.name, action: 'create' };
		return init;
	});

	const setAction = (name: string, action: 'create' | 'map'): void =>
		setDecisions((d) => ({ ...d, [keyOf(name)]: { ...d[keyOf(name)], name, action } }));
	const setMapTarget = (name: string, vendorId: string | null): void =>
		setDecisions((d) => ({ ...d, [keyOf(name)]: { name, action: 'map', vendorId: vendorId ?? undefined } }));

	// Every row valid: a 'create' always is; a 'map' needs a chosen vendor.
	const allResolved = useMemo(
		() => unresolved.every((u) => {
			const dec = decisions[keyOf(u.name)];
			return dec && (dec.action === 'create' || !!dec.vendorId);
		}),
		[unresolved, decisions],
	);

	return (
		<Modal visible={visible} onDismiss={onCancel} contentContainerStyle={styles.modal}>
			<Text style={styles.title}>Unrecognized payees</Text>
			<Text style={styles.subtitle}>
				{unresolved.length} {unresolved.length === 1 ? 'payee was' : 'payees were'} referenced by checks
				or bills but aren&rsquo;t in your vendor list. Create a vendor for each, or map it to an existing one.
			</Text>

			<ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
				{unresolved.map((u) => {
					const dec = decisions[keyOf(u.name)];
					const isMap = dec?.action === 'map';
					return (
						<View key={keyOf(u.name)} style={styles.row} testID={`uv-row-${keyOf(u.name)}`}>
							<View style={styles.rowHead}>
								<Text style={styles.payee}>{u.name}</Text>
								<Text style={styles.context}>
									{u.count} {u.count === 1 ? 'entry' : 'entries'} · e.g. {formatMoneyCents(u.sampleCents)} via {u.qbType}
								</Text>
							</View>
							<View style={styles.choiceRow}>
								<Pressable
									onPress={() => setAction(u.name, 'create')}
									style={[styles.chip, !isMap && styles.chipActive]}
									testID={`uv-create-${keyOf(u.name)}`}
								>
									<Text style={[styles.chipText, !isMap && styles.chipTextActive]}>Create new</Text>
								</Pressable>
								<Pressable
									onPress={() => setAction(u.name, 'map')}
									style={[styles.chip, isMap && styles.chipActive]}
									testID={`uv-map-${keyOf(u.name)}`}
								>
									<Text style={[styles.chipText, isMap && styles.chipTextActive]}>Map existing</Text>
								</Pressable>
							</View>
							{isMap ? (
								<LocalTypeahead
									items={vendors}
									toOption={(v) => ({ id: v.id, label: v.name })}
									value={dec?.vendorId ?? null}
									onChange={(id) => setMapTarget(u.name, id)}
									placeholder="Search existing vendors…"
									testID={`uv-typeahead-${keyOf(u.name)}`}
								/>
							) : (
								<Text style={styles.createHint}>Will create a vendor named “{u.name}”.</Text>
							)}
						</View>
					);
				})}
			</ScrollView>

			<View style={styles.actions}>
				<Button mode="text" onPress={onCancel}>Cancel</Button>
				<Button
					mode="contained"
					disabled={!allResolved}
					onPress={() => onResolve(unresolved.map((u) => decisions[keyOf(u.name)]))}
					testID="uv-confirm"
				>
					Continue import
				</Button>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { maxWidth: 560, width: '100%', alignSelf: 'center' },
	title: { fontSize: 18, fontWeight: '700', color: '#212121', marginBottom: 4 },
	subtitle: { fontSize: 13, color: '#78909c', marginBottom: 12 },
	list: { maxHeight: 420 },
	listContent: { gap: 12 },
	row: { gap: 8, paddingBottom: 12, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#e0e0e0' },
	rowHead: { gap: 2 },
	payee: { fontSize: 15, fontWeight: '600', color: '#212121' },
	context: { fontSize: 12, color: '#78909c' },
	choiceRow: { flexDirection: 'row', gap: 8 },
	chip: { paddingVertical: 6, paddingHorizontal: 14, borderRadius: 16, borderWidth: 1, borderColor: '#b0bec5' },
	chipActive: { backgroundColor: '#e3f2fd', borderColor: '#1565c0' },
	chipText: { fontSize: 13, color: '#546e7a' },
	chipTextActive: { color: '#1565c0', fontWeight: '600' },
	createHint: { fontSize: 12, color: '#78909c', fontStyle: 'italic' },
	actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
});
